import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../hooks/useNotification";
import { useDeleteCustomerSegmentMutation } from "../../../store/apiSlice/customerApiSlice";
import { ConfirmationModal } from "../../../components/organisms/Modal/confirmationModal";
import { componentWithCondition } from "../../newLayout/page/components/hoc/componentWithConditionCheck";
import { FormInput } from "../../../components/atoms/Input/Input";
import { FormLabel } from "../../../components/atoms/label";
import { ListActionBurgerMenu } from "../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";

const CustomerSegmentAction = ({ selectedOrderId, setSelectedOrder }) => {
  const navigate = useNavigate();
  const notify = useNotification();
  const [deleteSegment, { isLoading: isDeleteLoading } = {}] = useDeleteCustomerSegmentMutation();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [segmentName, setSegmentName] = useState("");

  const handleUpdate = () => {
    if (selectedOrderId.length !== 1) {
      notify({ type: "warning", message: "Please select exactly one segment to update." });
      return;
    }
    navigate(`/master/customer_segment/update/${selectedOrderId[0]}`);
  };

  const handleDelete = async () => {
    const results = await Promise.allSettled(selectedOrderId.map(id => deleteSegment(id).unwrap()));
    const failed = results.filter(r => r.status === "rejected");

    if (failed.length === 0) {
      notify({ type: "success", message: "Customer segment(s) deleted successfully." });
    } else if (failed.length === results.length) {
      notify({ type: "error", message: "All deletions failed. Please try again." });
    } else {
      notify({
        type: "warning",
        message: `${failed.length} of ${results.length} deletions failed.`,
      });
    }

    setSelectedOrder([]);
    setIsDeleting(false); // always close the modal
  };

  const handleCreateRedirect = () => {
    if (!segmentName.trim()) {
      notify({ type: "error", message: "Please enter a segment name." });
      return;
    }
    navigate("/master/customer_segment/create", { state: { name: segmentName } });
    setIsCreating(false);
    setSegmentName("");
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "delete",
        label: "Delete",
        onClick: () => setIsDeleting(true),
        disabled: selectedOrderId.length === 0 || isDeleteLoading,
        title: selectedOrderId.length > 0 ? "" : "Select at least one segment to delete.",
        isLoading: isDeleteLoading,
        isDanger: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOrderId.length, isDeleteLoading],
  );

  return (
    <>
      <li className={`tableButton activeState`}>
        <button onClick={() => setIsCreating(true)}>Create</button>
      </li>
      <li
        className={`tableButton ${selectedOrderId.length === 1 ? "activeState" : "disableState"}`}
      >
        <button
          onClick={handleUpdate}
          disabled={selectedOrderId.length !== 1}
          title={selectedOrderId.length === 1 ? "" : "Select a single segment to update."}
        >
          Update
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {isCreating && (
        <ConfirmationModal
          titleOfModal="Create Customer Segment"
          btnName="Create Segment"
          closeModal={() => setIsCreating(false)}
          handleModalWork={handleCreateRedirect}
          description={
            <>
              <FormLabel>Customer Segment Name</FormLabel>
              <FormInput
                autoFocus
                value={segmentName}
                onChange={e => setSegmentName(e.target.value)}
                placeholder="Enter Customer Segment Name"
              />
            </>
          }
        />
      )}

      {isDeleting && (
        <ConfirmationModal
          titleOfModal="Delete Customer Segment"
          btnName="Delete"
          closeModal={() => setIsDeleting(false)}
          handleModalWork={handleDelete}
          isLoading={isDeleteLoading}
          description={`Are you sure you want to delete ${selectedOrderId.length} selected segment(s)?`}
          labelForConfirmCheck="Confirm deletion"
        />
      )}
    </>
  );
};

export const CustomerSegmentActionWithCheck = componentWithCondition(CustomerSegmentAction);
